import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';
import type { AnalyticsUserEvent } from '@fruitful/types';

import { Singleton } from '../../common/utils/Singleton';
import { CUSTOMERIO_WEB_WRITE_KEY } from '../../config/config';
import { IS_LOCAL } from '../../config/FruitfulEnvironment';
import type { User } from '../../user/types/User';
import { getCustomerIOTraits } from '../utils/UserTraits';
import type { ICustomerIO } from './types/CustomerIO';

class CustomerIOFacade extends Singleton implements ICustomerIO {
    private analytics: AnalyticsBrowser | undefined;

    // eslint-disable-next-line @typescript-eslint/require-await -- sync Customer.io browser SDK init; interface requires Promise<void>
    public async init(): Promise<void> {
        if (!this.isCustomerIOEnabled()) {
            return;
        }

        this.analytics = AnalyticsBrowser.load({
            writeKey: CUSTOMERIO_WEB_WRITE_KEY as string,
        });
    }

    public async identifyUser(user: User): Promise<void> {
        if (!this.isCustomerIOEnabled()) {
            return;
        }

        if (!this.analytics) {
            await this.init();
        }

        if (this.analytics) {
            await this.analytics.identify(
                user.id,
                getCustomerIOTraits(user),
                user.visitorId != null && user.visitorId !== ''
                    ? { anonymousId: user.visitorId }
                    : undefined,
            );
        }
    }

    public async identifyVisitor(visitorId: string): Promise<void> {
        if (!this.isCustomerIOEnabled()) {
            return;
        }

        if (!this.analytics) {
            await this.init();
        }

        if (this.analytics) {
            void this.analytics.setAnonymousId(visitorId);
        }
    }

    public async track(
        event: AnalyticsUserEvent,
        properties?: Record<string, unknown>,
    ): Promise<void> {
        if (!this.isCustomerIOEnabled()) {
            return;
        }

        if (this.analytics) {
            await this.analytics.track(event, properties);
        }
    }

    public async screen(
        screen: string,
        properties?: Record<string, unknown>,
    ): Promise<void> {
        if (!this.isCustomerIOEnabled()) {
            return;
        }

        if (!this.analytics) {
            await this.init();
        }

        if (this.analytics) {
            await this.analytics.page(screen, properties);
        }
    }

    public reset() {
        if (!this.isCustomerIOEnabled() || !this.analytics) {
            return;
        }

        void this.analytics.reset();
    }

    private isCustomerIOEnabled(): boolean {
        return !IS_LOCAL && CUSTOMERIO_WEB_WRITE_KEY !== undefined;
    }
}

export default CustomerIOFacade.getInstance();
